import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Paper,
  Stack,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import FactoryIcon from "@mui/icons-material/Factory"
import ApartmentIcon from "@mui/icons-material/Apartment"
import DescriptionIcon from "@mui/icons-material/Description"
import WarningIcon from "@mui/icons-material/Warning"
import SaveIcon from "@mui/icons-material/Save"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"
import HomeIcon from "@mui/icons-material/Home"
import SettingsIcon from "@mui/icons-material/Settings"
import SearchIcon from "@mui/icons-material/Search"

// API
import {
  getTypes,
  createTaskType,
  updateTaskType,
  deleteTaskType,
} from "../../helpers/api_helper"

// Floor options for production sections
const FLOOR_OPTIONS = [
  { value: "Basement", label: "Basement", icon: "B" },
  { value: "Ground", label: "Ground Floor", icon: "G" },
  { value: "1", label: "Floor 1", icon: "1" },
  { value: "2", label: "Floor 2", icon: "2" },
  { value: "3", label: "Floor 3", icon: "3" },
  { value: "4", label: "Floor 4", icon: "4" },
  { value: "5", label: "Floor 5", icon: "5" },
]

const MuiSectionsSetup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  document.title = `${t("Sections Setup")} | FlexFlow ERP`

  // State
  const [sections, setSections] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState("create")
  const [selectedSection, setSelectedSection] = useState(null)

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sectionToDelete, setSectionToDelete] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    floor: "",
  })
  const [formErrors, setFormErrors] = useState({})

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  // Fetch sections on mount
  useEffect(() => {
    fetchSections()
  }, [])

  const fetchSections = async () => {
    setLoading(true)
    try {
      const data = await getTypes()
      setSections(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Error fetching sections:", error)
      showSnackbar(t("Failed to load sections"), "error")
    } finally {
      setLoading(false)
    }
  }

  // Show snackbar helper
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  // Filter sections by search
  const filteredSections = sections.filter((section) => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    return (
      section.name?.toLowerCase().includes(search) ||
      section.description?.toLowerCase().includes(search) ||
      section.floor?.toLowerCase().includes(search)
    )
  })

  // Paginated sections
  const paginatedSections = filteredSections.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  // Stats
  const stats = {
    total: sections.length,
    floorsUsed: new Set(sections.filter((s) => s.floor).map((s) => s.floor)).size,
    unassigned: sections.filter((s) => !s.floor).length,
    withDescription: sections.filter((s) => s.description).length,
  }

  // Open create dialog
  const handleOpenCreate = () => {
    setDialogMode("create")
    setFormData({ name: "", description: "", floor: "" })
    setFormErrors({})
    setSelectedSection(null)
    setDialogOpen(true)
  }

  // Open edit dialog
  const handleOpenEdit = (section) => {
    setDialogMode("edit")
    setFormData({
      name: section.name || "",
      description: section.description || "",
      floor: section.floor || "",
    })
    setFormErrors({})
    setSelectedSection(section)
    setDialogOpen(true)
  }

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedSection(null)
    setFormData({ name: "", description: "", floor: "" })
    setFormErrors({})
  }

  // Form change handler
  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  // Validate form
  const validateForm = () => {
    const errors = {}
    if (dialogMode === "create" && !formData.name.trim()) {
      errors.name = t("Section name is required")
    }
    if (!formData.floor) {
      errors.floor = t("Please select a floor")
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Save section
  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    try {
      if (dialogMode === "create") {
        const payload = {
          resource: [
            {
              name: formData.name.trim(),
              description: formData.description.trim(),
              floor: formData.floor,
            },
          ],
        }
        const result = await createTaskType(payload)
        if (result) {
          await fetchSections()
          showSnackbar(t("Section created successfully"))
          handleCloseDialog()
        }
      } else {
        const payload = {
          resource: [
            {
              id: selectedSection.id,
              description: formData.description.trim(),
              floor: formData.floor,
            },
          ],
        }
        await updateTaskType(payload)
        setSections((prev) =>
          prev.map((s) =>
            s.id === selectedSection.id
              ? { ...s, description: formData.description, floor: formData.floor }
              : s
          )
        )
        showSnackbar(t("Section updated successfully"))
        handleCloseDialog()
      }
    } catch (error) {
      console.error("Error saving section:", error)
      showSnackbar(t("Failed to save section"), "error")
    } finally {
      setSaving(false)
    }
  }

  // Open delete confirmation
  const handleOpenDelete = (section) => {
    setSectionToDelete(section)
    setDeleteDialogOpen(true)
  }

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!sectionToDelete) return

    setSaving(true)
    try {
      const payload = { resource: [{ id: sectionToDelete.id }] }
      await deleteTaskType(payload)
      setSections((prev) => prev.filter((s) => s.id !== sectionToDelete.id))
      showSnackbar(t("Section deleted successfully"))
    } catch (error) {
      console.error("Error deleting section:", error)
      showSnackbar(t("Failed to delete section"), "error")
    } finally {
      setSaving(false)
      setDeleteDialogOpen(false)
      setSectionToDelete(null)
    }
  }

  // Get floor chip styles
  const getFloorChipStyles = (floor) => {
    const styles = {
      Basement: { bgcolor: "#f3f4f6", color: "#374151" },
      Ground: { bgcolor: "#dbeafe", color: "#1d4ed8" },
      "1": { bgcolor: "#dcfce7", color: "#15803d" },
      "2": { bgcolor: "#e0f2fe", color: "#0369a1" },
      "3": { bgcolor: "#fef3c7", color: "#b45309" },
      "4": { bgcolor: "#f3e8ff", color: "#7c3aed" },
      "5": { bgcolor: "#fee2e2", color: "#dc2626" },
    }
    return styles[floor] || { bgcolor: "#f3f4f6", color: "#6b7280" }
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          border: "1px solid #e5e7eb",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Breadcrumbs separator="/" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
            <Link
              component={RouterLink}
              to="/"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", "&:hover": { color: "#3b82f6" } }}
            >
              <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </Link>
            <Link
              component={RouterLink}
              to="/settings"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.875rem", "&:hover": { color: "#3b82f6" } }}
            >
              <SettingsIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {t("Settings")}
            </Link>
            <Typography sx={{ display: "flex", alignItems: "center", color: "#111827", fontSize: "0.875rem", fontWeight: 500 }}>
              <FactoryIcon sx={{ fontSize: 14, mr: 0.5 }} />
              {t("Sections")}
            </Typography>
          </Breadcrumbs>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t("Refresh")}>
            <IconButton size="small" onClick={fetchSections} sx={{ color: "#6b7280" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              bgcolor: "#3b82f6",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              "&:hover": { bgcolor: "#2563eb" },
            }}
          >
            {t("Add Section")}
          </Button>
        </Stack>
      </Paper>

      {/* Stats Row */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{ flex: 1, px: 2, py: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
            <FactoryIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827" sx={{ lineHeight: 1.2 }}>
              {stats.total}
            </Typography>
            <Typography variant="caption" color="#6b7280">
              {t("Total Sections")}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{ flex: 1, px: 2, py: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#dcfce7", color: "#16a34a" }}>
            <ApartmentIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827" sx={{ lineHeight: 1.2 }}>
              {stats.floorsUsed}
            </Typography>
            <Typography variant="caption" color="#6b7280">
              {t("Floors Used")}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{ flex: 1, px: 2, py: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#fef3c7", color: "#d97706" }}>
            <WarningIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827" sx={{ lineHeight: 1.2 }}>
              {stats.unassigned}
            </Typography>
            <Typography variant="caption" color="#6b7280">
              {t("Unassigned")}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{ flex: 1, px: 2, py: 1.5, borderRadius: 1, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: "#e0e7ff", color: "#4f46e5" }}>
            <DescriptionIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#111827" sx={{ lineHeight: 1.2 }}>
              {stats.withDescription}
            </Typography>
            <Typography variant="caption" color="#6b7280">
              {t("With Description")}
            </Typography>
          </Box>
        </Paper>
      </Stack>

      {/* Search & Table */}
      <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Search Bar */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e7eb", bgcolor: "#f9fafb" }}>
          <TextField
            size="small"
            placeholder={t("Search sections...")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 280,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: 1,
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
              },
            }}
          />
        </Box>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem", textTransform: "uppercase", py: 1.5 }}>
                  {t("Section Name")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem", textTransform: "uppercase", py: 1.5 }}>
                  {t("Description")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem", textTransform: "uppercase", py: 1.5, width: 120 }}>
                  {t("Floor")}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#374151", fontSize: "0.75rem", textTransform: "uppercase", py: 1.5, width: 100 }} align="right">
                  {t("Actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginatedSections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#6b7280" }}>
                    {searchTerm ? t("No sections match your search") : t("No sections found")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSections.map((section) => (
                  <TableRow
                    key={section.id}
                    hover
                    sx={{
                      "&:hover": { bgcolor: "#f9fafb" },
                      "& td": { borderBottom: "1px solid #f3f4f6" },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FactoryIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={600} color="#111827">
                          {section.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#6b7280" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {section.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {section.floor ? (
                        <Chip
                          label={section.floor}
                          size="small"
                          sx={{
                            ...getFloorChipStyles(section.floor),
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="#9ca3af">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("Edit")}>
                          <IconButton size="small" onClick={() => handleOpenEdit(section)} sx={{ color: "#3b82f6" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("Delete")}>
                          <IconButton size="small" onClick={() => handleOpenDelete(section)} sx={{ color: "#ef4444" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredSections.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            borderTop: "1px solid #e5e7eb",
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
              fontSize: "0.813rem",
              color: "#6b7280",
            },
          }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: dialogMode === "create" ? "#dbeafe" : "#e0f2fe", color: dialogMode === "create" ? "#3b82f6" : "#0284c7" }}>
              {dialogMode === "create" ? <AddIcon /> : <EditIcon />}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {dialogMode === "create" ? t("Create New Section") : t("Edit Section")}
              </Typography>
              {dialogMode === "edit" && selectedSection && (
                <Typography variant="body2" color="#6b7280">
                  {selectedSection.name}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {dialogMode === "create" && (
              <TextField
                fullWidth
                required
                label={t("Section Name")}
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={Boolean(formErrors.name)}
                helperText={formErrors.name || t("Unique identifier for this section")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FactoryIcon sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              fullWidth
              required
              select
              label={t("Floor")}
              name="floor"
              value={formData.floor}
              onChange={handleFormChange}
              error={Boolean(formErrors.floor)}
              helperText={formErrors.floor || t("Assign this section to a floor")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ApartmentIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="" disabled>
                <em>{t("Select a floor")}</em>
              </MenuItem>
              {FLOOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={option.icon} size="small" sx={{ ...getFloorChipStyles(option.value), minWidth: 28 }} />
                    <span>{option.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("Description")}
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              helperText={t("Optional description of what happens in this section")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                    <DescriptionIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {saving ? t("Saving...") : dialogMode === "create" ? t("Create Section") : t("Save Changes")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "#fee2e2", color: "#dc2626" }}>
              <DeleteIcon />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              {t("Delete Section")}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            {t("This action cannot be undone.")}
          </Alert>
          <Typography>
            {t("Are you sure you want to delete the section")} <strong>{sectionToDelete?.name}</strong>?
          </Typography>
          {sectionToDelete?.description && (
            <Typography variant="body2" color="#6b7280" sx={{ mt: 1, fontStyle: "italic" }}>
              "{sectionToDelete.description}"
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {saving ? t("Deleting...") : t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%", borderRadius: 1 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MuiSectionsSetup
